<script lang="ts">
  /**
   * Modal showing the full chunked-memory sequence for a given memory id.
   *
   * mu-api groups chunks by (a) parent_id walk and (b) topic-stem match
   * — see /api/memory/:id/context. The dialog renders chunks in part-
   * number order, lets the user navigate part-by-part, and includes a
   * concatenated 'all chunks' view for full-context reads.
   */

  import { SvelteMap } from 'svelte/reactivity'
  import { fetchContext, fetchMemoryContent, type ContextResponse, type ContextChunk } from './api.svelte'
  import { selection } from './selection.svelte'

  type Props = {
    open: boolean
    sourceId: number | null
    onclose: () => void
  }
  let { open, sourceId, onclose }: Props = $props()

  let ctx = $state<ContextResponse | null>(null)
  let loading = $state(false)
  let error = $state<string | null>(null)
  let activeIdx = $state(0)
  let mode = $state<'single' | 'all'>('single')

  /* Per-chunk lazy-loaded full content. Keyed by memory id; presence
   * means the user clicked "load remaining" for that chunk and we have
   * the full body. Reactive map so the truncated banner / button swap
   * to full text without a re-render. */
  const fullContent = new SvelteMap<number, string>()
  const loadingFull = new SvelteMap<number, boolean>()

  async function loadRemaining(c: ContextChunk): Promise<void> {
    if (fullContent.has(c.id) || loadingFull.get(c.id)) return
    loadingFull.set(c.id, true)
    try {
      const res = await fetchMemoryContent(c.id)
      if (res) {
        fullContent.set(c.id, res.content)
      }
    } finally {
      loadingFull.set(c.id, false)
    }
  }

  /** Returns the visible content for a chunk: full if loaded, else the
   * server-truncated slice. */
  function chunkBody(c: ContextChunk): string {
    return fullContent.get(c.id) ?? c.contentFull
  }
  function chunkIsTruncated(c: ContextChunk): boolean {
    return c.contentTruncated && !fullContent.has(c.id)
  }

  $effect(() => {
    if (!open || sourceId == null) {
      ctx = null
      error = null
      activeIdx = 0
      return
    }
    loading = true
    error = null
    fetchContext(sourceId)
      .then((r) => {
        ctx = r
        if (!r) error = 'Could not load context'
        else {
          const i = r.chunks.findIndex((c) => c.id === sourceId)
          activeIdx = i >= 0 ? i : 0
        }
      })
      .finally(() => { loading = false })
  })

  function onKey(e: KeyboardEvent): void {
    if (!open) return
    if (e.key === 'Escape') { e.preventDefault(); onclose() }
    if (!ctx) return
    if (e.key === 'ArrowRight') {
      e.preventDefault()
      activeIdx = Math.min(ctx.chunks.length - 1, activeIdx + 1)
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      activeIdx = Math.max(0, activeIdx - 1)
    }
  }

  function partLabel(c: ContextChunk): string {
    if (c.partN != null && c.partTotal != null) return `Part ${c.partN}/${c.partTotal}`
    return `#${c.id}`
  }

  function selectChunkInConstellation(c: ContextChunk): void {
    selection.selectedId = c.id
  }
</script>

<svelte:window onkeydown={onKey} />

{#if open}
  <div class="ctx-backdrop" role="presentation" onclick={(e) => { if (e.target === e.currentTarget) onclose() }}>
    <div class="ctx-dialog" aria-label="Full context">
      <header class="ctx-head">
        <span class="ctx-tag">// full context</span>
        {#if ctx}
          <span class="ctx-stem">{ctx.stem ?? `(parent ${ctx.parentRoot})`}</span>
          <span class="ctx-count">{ctx.chunks.length} chunks · {ctx.strategy}</span>
        {/if}
        <div class="ctx-mode">
          <button class="ctx-mode-btn" class:on={mode === 'single'} type="button" onclick={() => mode = 'single'}>SINGLE</button>
          <button class="ctx-mode-btn" class:on={mode === 'all'} type="button" onclick={() => mode = 'all'}>ALL</button>
        </div>
        <button class="ctx-close" type="button" onclick={onclose} aria-label="Close">✕</button>
      </header>

      {#if loading}
        <div class="ctx-body ctx-loading">FETCHING CONTEXT…</div>
      {:else if error}
        <div class="ctx-body ctx-error">{error}</div>
      {:else if ctx && ctx.chunks.length === 0}
        <div class="ctx-body ctx-loading">— no related chunks —</div>
      {:else if ctx}
        {#if mode === 'single'}
          <nav class="ctx-strip">
            <button
              class="ctx-nav"
              type="button"
              disabled={activeIdx === 0}
              onclick={() => activeIdx = Math.max(0, activeIdx - 1)}
              title="Previous (←)"
            >‹</button>
            <div class="ctx-strip-list">
              {#each ctx.chunks as c, i (c.id)}
                <button
                  class="ctx-strip-tab"
                  class:on={i === activeIdx}
                  class:cur={c.id === sourceId}
                  type="button"
                  onclick={() => activeIdx = i}
                  title="#{c.id} {c.topic}"
                >{partLabel(c)}</button>
              {/each}
            </div>
            <button
              class="ctx-nav"
              type="button"
              disabled={activeIdx >= ctx.chunks.length - 1}
              onclick={() => { if (ctx) activeIdx = Math.min(ctx.chunks.length - 1, activeIdx + 1) }}
              title="Next (→)"
            >›</button>
          </nav>

          {@const c = ctx.chunks[activeIdx]}
          <div class="ctx-body">
            <div class="ctx-meta">
              <span class="ctx-meta-id">#{c.id}</span>
              <span class="ctx-meta-topic">{c.topic ?? '(no topic)'}</span>
              <span class="ctx-meta-pill">imp {c.importance ?? 0}</span>
              <span class="ctx-meta-pill">{c.contentLength.toLocaleString()} chars</span>
              <span class="ctx-meta-pill ctx-meta-match" data-match={c.matchedBy}>via {c.matchedBy}</span>
              <button
                class="ctx-meta-jump"
                type="button"
                onclick={() => selectChunkInConstellation(c)}
                title="Select this chunk in the constellation"
              >→ CONSTELLATION</button>
            </div>
            <pre class="ctx-content">{chunkBody(c)}</pre>
            {#if chunkIsTruncated(c)}
              <div class="ctx-load-row">
                <span class="ctx-load-status">
                  showing {c.contentFull.length.toLocaleString()} of {c.contentLength.toLocaleString()} chars
                </span>
                <button
                  class="ctx-load-btn"
                  type="button"
                  disabled={loadingFull.get(c.id)}
                  onclick={() => void loadRemaining(c)}
                >
                  {loadingFull.get(c.id) ? '◌ loading…' : '▼ load remaining'}
                </button>
              </div>
            {:else if c.contentTruncated && fullContent.has(c.id)}
              <div class="ctx-load-row">
                <span class="ctx-load-status ctx-load-done">✓ full {c.contentLength.toLocaleString()} chars loaded</span>
              </div>
            {/if}
          </div>
        {:else}
          <div class="ctx-body">
            {#each ctx.chunks as c, i (c.id)}
              <article class="ctx-chunk">
                <header class="ctx-chunk-head">
                  <span class="ctx-chunk-num">{i + 1} / {ctx.chunks.length}</span>
                  <span class="ctx-chunk-id">#{c.id}</span>
                  <span class="ctx-chunk-topic">{c.topic ?? '(no topic)'}</span>
                  <button class="ctx-meta-jump" type="button" onclick={() => selectChunkInConstellation(c)}>→</button>
                </header>
                <pre class="ctx-content ctx-chunk-content">{chunkBody(c)}</pre>
                {#if chunkIsTruncated(c)}
                  <div class="ctx-load-row">
                    <span class="ctx-load-status">
                      showing {c.contentFull.length.toLocaleString()} of {c.contentLength.toLocaleString()} chars
                    </span>
                    <button
                      class="ctx-load-btn"
                      type="button"
                      disabled={loadingFull.get(c.id)}
                      onclick={() => void loadRemaining(c)}
                    >
                      {loadingFull.get(c.id) ? '◌ loading…' : '▼ load remaining'}
                    </button>
                  </div>
                {/if}
              </article>
            {/each}
          </div>
        {/if}
      {/if}
    </div>
  </div>
{/if}

<style>
  .ctx-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.85);
    backdrop-filter: blur(3px);
    z-index: 220;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--s-3);
    animation: ctx-fade var(--t-mid);
  }
  @keyframes ctx-fade { from { opacity: 0 } to { opacity: 1 } }

  .ctx-dialog {
    width: 100%;
    max-width: 880px;
    height: 90vh;
    background: var(--surface);
    border: 1px solid var(--accent);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.7), 0 0 18px var(--accent-faint);
    display: flex;
    flex-direction: column;
    font-family: var(--font-mono);
  }

  .ctx-head {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    padding: var(--s-2) var(--s-3);
    border-bottom: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    flex-shrink: 0;
  }
  .ctx-tag { color: var(--accent); font-weight: 700; }
  .ctx-stem {
    color: var(--accent-bright);
    font-weight: 600;
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ctx-count { color: var(--on-surface-faint); font-size: 10px; }

  .ctx-mode {
    display: inline-flex;
    border: 1px solid var(--outline-variant);
  }
  .ctx-mode-btn {
    background: transparent;
    border: none;
    color: var(--on-surface-faint);
    font: inherit;
    font-size: 10px;
    padding: 4px 10px;
    cursor: pointer;
    transition: color var(--t-fast), background var(--t-fast);
  }
  .ctx-mode-btn:hover { color: var(--accent); }
  .ctx-mode-btn.on {
    color: var(--accent-bright);
    background: var(--accent-faint);
  }

  .ctx-close {
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    width: 24px; height: 24px;
    cursor: pointer;
    line-height: 1;
  }
  .ctx-close:hover { color: var(--accent); border-color: var(--accent); }

  .ctx-loading, .ctx-error {
    padding: var(--s-6);
    text-align: center;
    color: var(--on-surface-variant);
    letter-spacing: 0.16em;
  }
  .ctx-error { color: #ff6b6b; }

  /* Single mode strip */
  .ctx-strip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px var(--s-2);
    border-bottom: 1px dashed var(--outline-variant);
    background: var(--surface-container-lowest);
    flex-shrink: 0;
  }
  .ctx-nav {
    width: 28px;
    height: 28px;
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--accent);
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    flex-shrink: 0;
  }
  .ctx-nav:disabled { opacity: 0.3; cursor: not-allowed; }
  .ctx-nav:hover:not(:disabled) { background: var(--accent-faint); }

  .ctx-strip-list {
    flex: 1;
    display: flex;
    gap: 4px;
    overflow-x: auto;
    padding: 0 4px;
  }
  .ctx-strip-tab {
    flex-shrink: 0;
    padding: 4px 8px;
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-faint);
    font: inherit;
    font-size: 10px;
    letter-spacing: 0.06em;
    cursor: pointer;
    transition: color var(--t-fast), border-color var(--t-fast), background var(--t-fast);
  }
  .ctx-strip-tab:hover { color: var(--accent); border-color: var(--accent-soft); }
  .ctx-strip-tab.on {
    color: var(--accent-bright);
    border-color: var(--accent);
    background: var(--accent-faint);
  }
  .ctx-strip-tab.cur::before { content: '▸ '; color: var(--accent-bright); }

  .ctx-body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: var(--s-3);
    font-size: 12px;
    line-height: 1.5;
    color: var(--on-surface);
  }

  .ctx-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
    margin-bottom: var(--s-3);
    padding-bottom: var(--s-2);
    border-bottom: 1px dashed var(--outline-variant);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .ctx-meta-id { color: var(--accent); font-weight: 700; }
  .ctx-meta-topic {
    color: var(--accent-bright);
    flex: 1;
    text-transform: none;
    letter-spacing: 0;
    font-size: 12px;
  }
  .ctx-meta-pill {
    padding: 2px 6px;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-variant);
    font-size: 9px;
  }
  .ctx-meta-match[data-match='parent'] { color: var(--accent); border-color: var(--accent-soft); }
  .ctx-meta-match[data-match='stem']   { color: var(--accent-bright); border-color: var(--accent); border-style: dashed; }

  .ctx-meta-jump {
    background: transparent;
    border: 1px solid var(--accent-soft);
    color: var(--accent);
    font: inherit;
    font-size: 9px;
    letter-spacing: 0.08em;
    padding: 2px 8px;
    cursor: pointer;
  }
  .ctx-meta-jump:hover { background: var(--accent-faint); color: var(--accent-bright); }

  .ctx-content {
    margin: 0;
    padding: var(--s-3);
    background: var(--surface-container-lowest);
    border: 1px solid var(--outline-variant);
    font-family: inherit;
    font-size: 12px;
    line-height: 1.55;
    color: var(--on-surface);
    white-space: pre-wrap;
    word-break: break-word;
  }

  .ctx-chunk { margin-bottom: var(--s-4); }
  .ctx-chunk-head {
    display: flex;
    align-items: baseline;
    gap: var(--s-2);
    margin-bottom: 4px;
    padding-bottom: 4px;
    border-bottom: 1px dashed var(--outline-variant);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .ctx-chunk-num {
    color: var(--accent-bright);
    font-weight: 700;
  }
  .ctx-chunk-id { color: var(--accent); }
  .ctx-chunk-topic {
    flex: 1;
    color: var(--on-surface);
    text-transform: none;
    letter-spacing: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .ctx-chunk-content { font-size: 11px; }

  /* ── Load-remaining row ─────────────────────────────────────── */
  .ctx-load-row {
    display: flex;
    align-items: center;
    gap: var(--s-3);
    margin-top: 4px;
    padding: 6px var(--s-3);
    background: var(--surface-container-low);
    border: 1px dashed var(--accent-soft);
    border-top: none;
  }
  .ctx-load-status {
    flex: 1;
    color: var(--on-surface-faint);
    font-size: 10px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
  .ctx-load-status.ctx-load-done {
    color: var(--accent);
  }
  .ctx-load-btn {
    background: transparent;
    border: 1px solid var(--accent);
    color: var(--accent);
    font: inherit;
    font-size: 10px;
    letter-spacing: 0.1em;
    padding: 4px 12px;
    cursor: pointer;
    transition: background var(--t-fast), color var(--t-fast);
  }
  .ctx-load-btn:hover:not(:disabled) {
    background: var(--accent);
    color: var(--surface);
  }
  .ctx-load-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
